"use client";

import React from "react";
import { Briefcase, Building2, Landmark, MapPinned, ShieldAlert, Users } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const audiences = [{
  name: "First Nations",
  icon: Landmark,
  note: "We work closely with Chief and Council, senior management, and community staff to support governance, reporting, financial recovery, and long-term organizational stability."
}, {
  name: "Northern and Remote Communities",
  icon: MapPinned,
  note: "Northern organizations often operate under unique geographic, staffing, and funding pressures. We help build systems, reporting, and management practices that fit real-world operating conditions."
}, {
  name: "Community-Owned Organizations",
  icon: Building2,
  note: "We support corporations, limited partnerships, and other community-owned entities with financial oversight, reporting, governance support, and practical business structures."
}, {
  name: "Economic Development Entities",
  icon: Briefcase,
  note: "We help communities and development groups build strong ownership, partnership, and operating structures that reflect community priorities and support long-term growth."
}, {
  name: "Leadership and Management Teams",
  icon: Users,
  note: "We support leadership teams managing growth, backlog, changing requirements, funding complexity, or internal capacity gaps by helping create clarity and move work forward."
}, {
  name: "Organizations in Financial Recovery",
  icon: ShieldAlert,
  note: "When audits, reporting, or financial systems have fallen behind, we help restore order, rebuild momentum, and create a clear path toward audit readiness and stronger financial management."
}];
export default function WhoWeServe() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="142066" id="who-we-serve" ref={ref} className="bg-primary-50/40 text-foreground py-16 md:py-24 overflow-hidden relative">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(53,128,121,0.08),transparent_36%)] pointer-events-none" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_right,rgba(17,47,75,0.05),transparent_42%)] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className={cn("max-w-3xl mx-auto text-center mb-14 transition-all duration-700 transform", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10")}>
          <p className="text-secondary font-default font-bold tracking-widest uppercase text-sm mb-4">
            Who We Serve
          </p>

          <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold text-primary-500 mb-6 leading-tight">
            Practical support for organizations facing complex work.
          </h2>

          <p className="text-lg text-muted-foreground font-default leading-relaxed">
            RiverBend works alongside organizations facing complex financial,
            operational, and governance challenges. Our work is built for
            Northern realities and shaped by practical experience supporting
            First Nation communities, community-owned entities, and leadership
            teams that need clear systems, dependable follow-through, and
            respectful collaboration.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 xl:gap-8">
          {audiences.map((audience, index) => {
          const Icon = audience.icon;
          return <div key={audience.name} data-index={index} className={cn("h-full transition-all duration-700", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10")} style={{
            transitionDelay: `${index * 100}ms`
          }}>
                <Card className="h-full bg-background/95 border border-border shadow-sm hover:shadow-md hover:border-secondary/40 transition-all duration-300 rounded-lg overflow-hidden">
                  <CardContent className="p-7 md:p-8 flex flex-col h-full">
                    <div className="size-14 rounded-full bg-secondary/10 flex items-center justify-center mb-6">
                      <Icon className="size-7 text-secondary" strokeWidth={1.7} />
                    </div>

                    <h3 className="text-xl md:text-2xl font-default font-bold text-primary-500 mb-4 leading-snug">
                      {audience.name}
                    </h3>

                    <p className="text-muted-foreground font-default leading-relaxed">
                      {audience.note}
                    </p>
                  </CardContent>
                </Card>
              </div>;
        })}
        </div>
      </div>
    </section>;
}